import React, {useEffect, useLayoutEffect, useState, useRef, useMemo} from 'react';
import {Product} from '@amplify/athena-models';
import {makeStyles} from '@material-ui/core/styles';
import {savePrequalRecommendations} from './savePrequalRecommendations';
import {
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
    Button as MUIButton,
    Box,
} from '@material-ui/core';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {Spacing} from '../../Core/UI';
import ClientGoals from './Components/ClientGoals';
import BasicInfo from './Components/BasicInfo';
import Behavior from './Components/Behavior';
import Finances from './Components/Finances';
import HealthInfo from './Components/Health';
import {ClientQuoteData} from '../../APIs/CRM';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import {Button} from '@/components/ui/button';
import RootStore from '@/Core/mobx/RootStore';
import {SsnForm} from '@/APIs/types';
import {generatePath, useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import {SelectInputElement2} from './Components/SelectInputElement2';
import {BestFitProduct} from './Components/BestFitProduct';
import {WalkthroughRecommendationSummary} from './Components/WalkthroughRecommendationSummary';
import CRM from '../../APIs/CRM';
import CustomerPortalAPI from '../../APIs/CustomerPortalAPI';
import {ArrowBackIcon} from '../../components/icons/arrow-back';
import {determineProductTypeRecommendations} from './utils/recommendationLogic';

interface PrequalPanelParams {
    customerQuoteData?: ClientQuoteData[];
    valueChanged: (p?: Parameter, value?: string | number | null) => void;
    parameterStore: ParameterStore;
    contactsFormStore: typeof RootStore.agentPortalStore.contactsFormStore;
}

const useStyles = makeStyles({
    navigationButtons: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 30,
        padding: '20px 0',
        gap: 16,
    },
    stepIndicator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        marginBottom: 20,
        fontSize: 18,
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: '120%',
        letterSpacing: 0,
        textTransform: 'capitalize',
        color: '#6b7280',
        '& .active': {
            color: '#456d69',
            fontFamily: 'FredokaBold',
        },
    },
    backButton: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        minWidth: 56,
        padding: 0,
        border: '2px solid #4a5568',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: '#f7f7f7',
        },
        '& .MuiButton-label': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& svg': {
            width: 20,
            height: 14,
        },
    },
    nextButton: {
        borderRadius: 28,
        padding: '12px 48px',
        fontSize: 18,
        fontFamily: 'FredokaMedium',
        fontWeight: 500,
        textTransform: 'uppercase',
        backgroundColor: '#d1d5db',
        color: '#6b7280',
        '&:hover': {
            backgroundColor: '#9ca3af',
        },
        '&:disabled': {
            backgroundColor: '#d1d5db',
            color: '#6b7280',
        },
        '&.enabled': {
            backgroundColor: '#456d69',
            color: 'white',
            '&:hover': {
                backgroundColor: '#303f9f',
            },
        },
    },
});

interface MatchParams {
    tabIndex: string;
}

const FORM_TYPE_MAPPING: Record<string, 'prequal' | 'quote' | 'preapproval'> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'preapproval',
};

const steps = ['Goals & Basics', 'Eligibility', 'Finances', 'Best Fit Product'];

let resolveConfirmSave: (confirm: boolean) => void;

// Helper to get a value from a nested path
const getFieldValue = (store: any, path: string): any => {
    const parts = path.split('.');
    let current = store;
    for (const part of parts) {
        current = current?.[part];
        if (current === undefined) return undefined;
    }
    return current?.value;
};

// Step validation functions - using the actual validation logic from preQualValidation.tsx
const validateStep0 = (parameterStore: any): boolean => {
    // Goals/Basics - required fields
    const requiredFields = [
        // Goals
        {path: 'prequal.intentFocus'},
        {path: 'prequal.intentFunding'},
        // Basic Information
        {path: 'prequal.firstName'},
        {path: 'prequal.lastName'},
        {path: 'prequal.state'},
        {path: 'prequal.gender'},
        {path: 'prequal.age'},
        {path: 'prequal.usCitizenship'},
    ];

    const isVisaHolder = getFieldValue(parameterStore, 'prequal.usCitizenship') === 'USVisaHolder';
    if (isVisaHolder) {
        requiredFields.push({path: 'prequal.usVisaType'});
    }

    const missingFields = requiredFields.filter((field) => {
        const value = getFieldValue(parameterStore, field.path);
        if (typeof value === 'boolean') return false;
        return value === null || value === undefined || value === '';
    });

    return missingFields.length === 0;
};

const validateStep1 = (parameterStore: any): boolean => {
    // Eligibility validation - Basic Info + Health + Behavior
    const requiredFields = [
        // Health section required fields
        {path: 'prequal.tobacco'},
        {path: 'prequal.height'},
        {path: 'prequal.weight'},

        // Health conditions
        {path: 'health.WeightLossObesityGastric'},
        {path: 'health.SleepApnea'},
        {path: 'health.Cancer'},
        {path: 'health.HIVAIDS'},
        {path: 'health.AtrialFibulationAfib'},
        {path: 'health.BipolarDisorder'},
        {path: 'health.Schizophrenia'},
        {path: 'health.Defibrillator'},
        {path: 'health.Transplant'},

        // Behavior section required fields
        {path: 'prequal.DrugorAlcoholDependenceWithdrawal'},
        {path: 'prequal.DUI'},
        {path: 'prequal.License'},
        {path: 'prequal.Criminaloffense'},
        {path: 'prequal.Bankruptcy'},
    ];

    // Add conditional fields
    const tobacco = getFieldValue(parameterStore, 'prequal.tobacco');
    if (tobacco === true || tobacco === 'true') {
        requiredFields.push(
            {path: 'prequal.tobaccoUsage'},
            {path: 'prequal.tobaccoUsageDate'},
            {path: 'prequal.tobaccoMethod'},
        );
    }

    const sleepApnea = getFieldValue(parameterStore, 'health.SleepApnea');
    if (sleepApnea === true || sleepApnea === 'true') {
        requiredFields.push({path: 'health.sleepapneaSeverity'});
    }

    const cancer = getFieldValue(parameterStore, 'health.Cancer');
    if (cancer === true || cancer === 'true') {
        requiredFields.push({path: 'health.cancerStage'});
    }

    const drugAlcohol = getFieldValue(parameterStore, 'prequal.DrugorAlcoholDependenceWithdrawal');
    if (drugAlcohol === true || drugAlcohol === 'true') {
        requiredFields.push(
            {path: 'prequal.DrugorAlcoholDependenceWithdrawalTreatmentStatus'},
            {path: 'prequal.DrugorAlcoholDependenceWithdrawalTreatmentDate'},
        );
    }

    const dui = getFieldValue(parameterStore, 'prequal.DUI');
    if (dui === true || dui === 'true') {
        requiredFields.push({path: 'prequal.DUIDate'});
    }

    const license = getFieldValue(parameterStore, 'prequal.License');
    if (license === true || license === 'true') {
        requiredFields.push({path: 'prequal.suspendedLicense'});
    }

    const criminal = getFieldValue(parameterStore, 'prequal.Criminaloffense');
    if (criminal === true || criminal === 'true') {
        requiredFields.push(
            {path: 'prequal.criminalOffenseDate'},
            {path: 'prequal.criminalOffenseChargeType'},
            {path: 'prequal.criminalOffenseProbation'},
        );
    }

    const bankruptcy = getFieldValue(parameterStore, 'prequal.Bankruptcy');
    if (bankruptcy === true || bankruptcy === 'true') {
        requiredFields.push({path: 'prequal.bankruptcyOpen'});
    }

    // Check if all required fields have values
    // For boolean fields (Yes/No questions), false is a valid value
    const missingFields = requiredFields.filter((field) => {
        const value = getFieldValue(parameterStore, field.path);
        // For boolean fields, both true and false are valid values
        if (typeof value === 'boolean') {
            return false; // Field is valid
        }
        return value === null || value === undefined || value === '';
    });

    return missingFields.length === 0;
};

const validateStep2 = (parameterStore: any): boolean => {
    // Finances validation
    const requiredFields = [
        {path: 'prequal.incomeHouseholdText'},
        {path: 'prequal.existingCoverage'},
        {path: 'prequal.isCurrentlyMilitary'},
    ];

    // Add conditional fields
    const military = getFieldValue(parameterStore, 'prequal.isCurrentlyMilitary');
    if (military === true || military === 'true') {
        requiredFields.push({path: 'prequal.currentDeploymentOrders'});
    }

    const existingCoverage = getFieldValue(parameterStore, 'prequal.existingCoverage');
    if (existingCoverage === true || existingCoverage === 'true') {
        requiredFields.push(
            {path: 'prequal.existingPolicyType'},
            {path: 'prequal.existingCoverageAmount'},
            {path: 'prequal.replacingExistingPolicy'},
        );
    }

    // Check if all required fields have values
    // For boolean fields (Yes/No questions), false is a valid value
    const missingFields = requiredFields.filter((field) => {
        const value = getFieldValue(parameterStore, field.path);
        // For boolean fields, both true and false are valid values
        if (typeof value === 'boolean') {
            return false; // Field is valid
        }
        return value === null || value === undefined || value === '';
    });

    return missingFields.length === 0;
};

const validateStep3 = (): boolean => {
    // Best Fit Product - no required fields
    return true;
};

export const PrequalPanelMultiStep = observer(
    ({
        valueChanged,
        customerQuoteData: initialCustomerQuoteData,
        parameterStore,
        contactsFormStore,
    }: PrequalPanelParams) => {
        const {prequal, health} = parameterStore || {};
        const {historyFormId} = useParams<any>();

        const [loading, setLoading] = useState(false);
        const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
        const [duplicateSsnForms, setDuplicateSsnForms] = useState<SsnForm[]>([]);
        const [customerQuoteData, setCustomerQuoteData] = useState<ClientQuoteData[]>(initialCustomerQuoteData || []);
        const [productsLoading, setProductsLoading] = useState(false);

        // Initialize activeStep from parameterStore (for page refresh persistence)
        const [activeStep, setActiveStep] = useState(() => {
            return parameterStore?.prequalCurrentStep || 0;
        });

        // Fetch walkthrough data and customer quotes on load so WalkthroughRecommendationSummary has data on the first page
        useEffect(() => {
            const email = parameterStore?.contact || parameterStore?.walkthroughData?.email;
            if (!email) return;
            CRM.getCRMWalkthroughData(email)
                .then(({data}) => {
                    runInAction(() => {
                        parameterStore.walkthroughRawData = data;
                    });
                })
                .catch(() => {
                    // Walkthrough may not exist yet; leave walkthroughRawData as-is
                });
            CustomerPortalAPI.GetCustomerQuotes(email)
                .then((quoteData) => setCustomerQuoteData(quoteData || []))
                .catch(() => {});
        }, [parameterStore?.contact, parameterStore?.walkthroughData?.email]);

        // Fetch recommended products when reaching Best Fit Product page
        useEffect(() => {
            if (activeStep === 3 && parameterStore && !productsLoading) {
                // Fetch recommended products for Best Fit Product page
                const fetchProducts = async () => {
                    try {
                        setProductsLoading(true);

                        // Clear previous recommendations to avoid showing stale data
                        if (parameterStore.prequal) {
                            parameterStore.prequal.recommendedProductPrimary.setValue('');
                            parameterStore.prequal.recommendedProductFallback.setValue('');
                            parameterStore.prequal.recommendedProductFallbackTriggers.setValue('');
                            parameterStore.prequal.recommendedProductPrimaryScript.setValue('');
                            parameterStore.prequal.recommendedProductFallbackScript.setValue('');
                        }

                        // Also fetch customer quote data if we have an email
                        if (parameterStore.contact) {
                            CustomerPortalAPI.GetCustomerQuotes(parameterStore.contact)
                                .then((quoteData) => {
                                    setCustomerQuoteData(quoteData || []);
                                })
                                .catch((error) => {
                                    console.error('Failed to fetch customer quotes:', error);
                                });
                        }

                        // Fetch recommended products (this should populate qualifiedProducts and recommendedProductsResponse)
                        await parameterStore.fetchRecommendedProducts();

                        // After products are fetched, determine the product type recommendations.
                        // Always call this (even with empty/null products) so fallbackTriggers is
                        // set correctly and BestFitProduct can show "No product suggestion".
                        {
                            const eligibleCodes =
                                parameterStore.recommendedProductsResponse?.allEligibleProductCodes || [];
                            const allProducts = parameterStore.recommendedProductsResponse?.allProducts || [];
                            const eligibleProducts = allProducts.filter((p: any) => eligibleCodes.includes(p.code));
                            const productEligibility = {
                                iulProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'IUL'),
                                termProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'TERM'),
                                vulProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'VUL'),
                                finalExpenseProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'FEXP'),
                                annuityProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'ANNUITY'),
                                adbProducts: eligibleProducts.filter((p: any) => p.productTypeCode === 'ADB'),
                            };
                            determineProductTypeRecommendations(parameterStore.prequal, productEligibility);
                        }

                        // Force a re-render to ensure MobX picks up the changes
                        setProductsLoading(false);
                    } catch (error) {
                        console.error('Failed to fetch recommended products:', error);
                        // Even on error, mark recommendations as "no product eligible" so the
                        // UI doesn't hang on "Loading..."
                        if (parameterStore.prequal) {
                            determineProductTypeRecommendations(parameterStore.prequal, {
                                iulProducts: [],
                                termProducts: [],
                                vulProducts: [],
                                finalExpenseProducts: [],
                                annuityProducts: [],
                                adbProducts: [],
                            });
                        }
                        setProductsLoading(false);
                    }
                };
                fetchProducts();
            }
        }, [activeStep]);

        const match = useRouteMatch<MatchParams>();
        const history = useHistory();

        const formType = FORM_TYPE_MAPPING[match.params.tabIndex];

        const confirmSave = async (): Promise<boolean> => {
            const response = await new Promise<boolean>((resolve) => {
                resolveConfirmSave = resolve;
                setSaveConfirmationOpen(true);
            });

            setSaveConfirmationOpen(false);

            return response;
        };

        const confirmPendingSave = () => {
            resolveConfirmSave(true);
        };

        const cancelPendingSave = () => {
            resolveConfirmSave(false);
        };

        // Update the step in parameterStore whenever it changes
        useEffect(() => {
            if (parameterStore) {
                parameterStore.prequalCurrentStep = activeStep;
            }
        }, [activeStep, parameterStore]);

        // ScriptAssistStore prequalStep allows null to determine if the prequal flow is active or not.
        useLayoutEffect(() => {
            RootStore.scriptAssistStore.setPrequalStep(parameterStore.prequalCurrentStep);
            return () => {
                RootStore.scriptAssistStore.setPrequalStep(null);
            };
        }, []);

        // Save form data on each step navigation
        const saveStepData = async (destinationStep?: number) => {
            setLoading(true);
            try {
                // Update the current step before saving
                parameterStore.prequalCurrentStep = destinationStep ?? activeStep;
                await contactsFormStore.saveForm(formType);
            } catch (error) {
                console.error('Error saving step data:', error);
                RootStore.uiStore.showSnackbar('Error saving form data', 'error');
            } finally {
                setLoading(false);
            }
        };

        const saveForm = async (selectedProduct?: string) => {
            setLoading(true);

            // Save the selected product if provided
            if (selectedProduct && prequal?.selectedProduct) {
                prequal.selectedProduct.setValue(selectedProduct);
            }

            const formsWithSameSsn =
                await RootStore.agentPortalStore.contactsFormStore.getSsnFormsAssociatedWithCurrentFormSsn();
            const duplicateSsnForms = formsWithSameSsn.filter((form) => form.formId !== parameterStore.formid);

            if (duplicateSsnForms.length > 0) {
                setDuplicateSsnForms(duplicateSsnForms);
                if (!(await confirmSave())) {
                    setLoading(false);
                    return;
                }
            }

            const form = await contactsFormStore.saveForm(formType);
            const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
                email: form?.contact,
                formId: form?.formid,
                tabIndex: match.params.tabIndex,
            });
            window.history.replaceState(null, '', newPath);
            history.push(newPath);
            RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
            setLoading(false);
        };

        // Check if current step is valid
        // Since this component is wrapped with observer(), it will re-render
        // when MobX observable values change, so we don't need complex dependency tracking
        const isStepValid = (() => {
            const result = (() => {
                switch (activeStep) {
                    case 0:
                        return validateStep0(parameterStore);
                    case 1:
                        return validateStep1(parameterStore);
                    case 2:
                        return validateStep2(parameterStore);
                    case 3:
                        return validateStep3();
                    default:
                        return false;
                }
            })();

            return result;
        })();

        const handleNext = async () => {
            if (isStepValid) {
                // Update to next step immediately
                const nextStep = activeStep + 1;
                setActiveStep(nextStep);
                parameterStore.prequalCurrentStep = nextStep;
                RootStore.scriptAssistStore.setPrequalStep(nextStep);

                // Save in the background (don't await)
                saveStepData(nextStep).catch((error) => {
                    console.error('Error saving form:', error);
                    RootStore.uiStore.showSnackbar('Error saving progress', 'error');
                });
            }
        };

        const handleBack = async () => {
            // Update to previous step immediately
            const prevStep = activeStep - 1;
            setActiveStep(prevStep);
            parameterStore.prequalCurrentStep = prevStep;
            RootStore.scriptAssistStore.setPrequalStep(prevStep);

            // Save in the background (don't await)
            saveStepData(prevStep).catch((error) => {
                console.error('Error saving form:', error);
            });
        };

        // Add keyboard navigation
        useEffect(() => {
            const handleKeyPress = (e: KeyboardEvent) => {
                if (e.ctrlKey || e.metaKey) {
                    if (e.key === 'ArrowRight' && activeStep < steps.length - 1 && isStepValid && !loading) {
                        handleNext();
                    } else if (e.key === 'ArrowLeft' && activeStep > 0 && !loading) {
                        handleBack();
                    }
                }
            };

            window.addEventListener('keydown', handleKeyPress);
            return () => window.removeEventListener('keydown', handleKeyPress);
        }, [activeStep, isStepValid, loading]);

        const walkthroughRawData = (parameterStore as any)._walkthroughRawData;
        const investmentGoals = parameterStore.walkthroughData?.investmentGoals || [];

        // Separate useEffect to handle saving recommendations
        const lastSavedRecommendation = useRef<string>('');
        const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const primaryRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductPrimary?.value ?? null;
        }, [prequal?.recommendedProductPrimary?.value]);

        const fallbackRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductFallback?.value ?? null;
        }, [prequal?.recommendedProductFallback?.value]);

        useEffect(() => {
            // Only save if we have recommendations
            if (!primaryRecommendationValue) return;
            const email = parameterStore?.contact || parameterStore?.walkthroughData?.email;
            const customerId = parameterStore?.formid || '';
            // Create a unique key for this recommendation
            const recommendationKey = `${primaryRecommendationValue}-${fallbackRecommendationValue || ''}`;
            // Skip if same as last saved
            if (recommendationKey === lastSavedRecommendation.current) {
                return;
            }
            // Clear existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Debounce the save
            saveTimeoutRef.current = setTimeout(() => {
                if (!email) {
                    return;
                }
                const prequalParameters = {
                    age: prequal.age?.value,
                    gender: prequal.gender?.value,
                    state: prequal.state?.value,
                    tobacco: prequal.tobacco?.value,
                    height: prequal.height?.value,
                    weight: prequal.weight?.value,
                    incomeHouseholdText: prequal.incomeHouseholdText?.value,
                    coverageAmount: prequal.recommendedCoverageAmount?.value,
                    monthlyBudget: prequal.monthlyBudget?.value,
                    intentFocus: prequal.intentFocus?.value,
                    intentFunding: prequal.intentFunding?.value,
                    intentBenefitStart: prequal.intentBenefitStart?.value,
                };
                savePrequalRecommendations({
                    email,
                    customerId,
                    primaryRecommendation: primaryRecommendationValue,
                    primaryScript: prequal.recommendedProductPrimaryScript?.value,
                    fallbackRecommendation: fallbackRecommendationValue || '',
                    fallbackScript: prequal.recommendedProductFallbackScript?.value,
                    fallbackTriggers: prequal.recommendedProductFallbackTriggers?.value,
                    ineligibleForOriginal:
                        prequal.recommendedProductIneligibleForOriginalRecommendations?.value === 'true',
                    prequalParameters,
                    intentFocus: prequal.intentFocus?.value,
                    householdIncome: prequal.incomeHouseholdText?.value
                        ? Number(prequal.incomeHouseholdText?.value)
                        : undefined,
                    age: prequal.age?.value,
                });
                lastSavedRecommendation.current = recommendationKey;
            }, 3000);
            // Cleanup
            return () => {
                if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                }
            };
        }, [primaryRecommendationValue, fallbackRecommendationValue]);

        if (parameterStore === null) return null;

        const classes = useStyles();

        const required = (
            <span>
                <span style={{color: 'red'}}>*</span>
            </span>
        );

        const renderStepContent = () => {
            switch (activeStep) {
                case 0:
                    // Goals & Basics
                    return (
                        <>
                            <ClientGoals investmentGoals={investmentGoals} />

                            <hr style={{border: 'none', borderTop: '1px solid #E7E7E7', margin: '16px 0'}} />

                            <WalkthroughRecommendationSummary
                                parameterStore={parameterStore}
                                customerQuoteData={customerQuoteData}
                            />

                            <hr style={{border: 'none', borderTop: '1px solid #E7E7E7', margin: '8px 0 16px'}} />

                            <CardContent style={{padding: '0 16px'}}>
                                <div>
                                    <strong style={{fontSize: 18, fontWeight: 500, color: '#838383'}}>Questions</strong>
                                </div>
                                <Grid container spacing={3} style={{marginTop: 10}}>
                                    <Grid item xs={8}>
                                        <Typography>
                                            If you had to choose one, would you say your focus is more on saving for
                                            long term, getting retirement income in the next 15 years, maximizing life
                                            insurance protection right now, or passing down an inheritance?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <SelectInputElement2
                                            param={{
                                                label: 'Main Focus',
                                                options: fieldOptions.IntentFocus.options,
                                                optionValues: fieldOptions.IntentFocus.optionValues,
                                                ...prequal.intentFocus,
                                                name: 'intentFocus',
                                            }}
                                            required={required}
                                            valueChanged={valueChanged}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={3} style={{marginTop: 10}}>
                                    <Grid item xs={8}>
                                        <Typography>
                                            When you picture funding a plan like this, would you prefer a one-time lump
                                            sum, monthly contributions, or a mix of both?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <SelectInputElement2
                                            param={{
                                                label: 'Funding Plan',
                                                options: fieldOptions.IntentFunding.options,
                                                optionValues: fieldOptions.IntentFunding.optionValues,
                                                ...prequal.intentFunding,
                                                name: 'intentFunding',
                                            }}
                                            required={required}
                                            valueChanged={valueChanged}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>

                            <hr style={{border: 'none', borderTop: '1px solid #E7E7E7', margin: '16px 0'}} />

                            <BasicInfo parameters={prequal} valueChanged={valueChanged} hideNumbers={true} />
                        </>
                    );
                case 1:
                    // Eligibility
                    return (
                        <div style={{marginTop: 24}}>
                            <HealthInfo
                                parameters={prequal}
                                healthParameters={health}
                                valueChanged={valueChanged}
                                hideNumbers={true}
                            />
                            <Behavior parameters={prequal} valueChanged={valueChanged} hideNumbers={true} />
                        </div>
                    );
                case 2:
                    // Finances
                    return (
                        <Finances
                            walkthroughData={walkthroughRawData}
                            parameters={prequal}
                            valueChanged={valueChanged}
                            hideNumbers={true}
                        />
                    );
                case 3:
                    // Best Fit Product
                    return (
                        <BestFitProduct
                            parameterStore={parameterStore}
                            customerQuoteData={customerQuoteData}
                            onSave={saveForm}
                            loading={productsLoading}
                            hideUpdateButton={true}
                        />
                    );
                default:
                    return null;
            }
        };

        return (
            <Grid container spacing={2}>
                <Grid item xs={8} sm={9} md={9}>
                    <Spacing height={20} />
                    <Card style={{padding: 20, marginBottom: 20}}>
                        {/* Step indicator */}
                        <Box className={classes.stepIndicator}>
                            {steps.map((label, index) => (
                                <React.Fragment key={label}>
                                    <span className={index === activeStep ? 'active' : ''}>{label}</span>
                                    {index < steps.length - 1 && <span>/</span>}
                                </React.Fragment>
                            ))}
                        </Box>

                        {activeStep === 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#FFF0F0',
                                    borderRadius: 6,
                                    padding: '4px 0',
                                    marginBottom: 8,
                                }}
                            >
                                <span style={{height: 1, backgroundColor: '#E53935', flex: 1, marginLeft: 16}} />
                                <p
                                    style={{
                                        color: '#C62828',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        padding: '12px 18px',
                                        textTransform: 'uppercase',
                                        fontSize: 14,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    We&apos;re on a recorded line
                                </p>
                                <span style={{height: 1, backgroundColor: '#E53935', flex: 1, marginRight: 16}} />
                            </div>
                        )}

                        <Box minHeight="400px">{renderStepContent()}</Box>

                        <Box className={classes.navigationButtons}>
                            {activeStep > 0 && (
                                <MUIButton
                                    className={classes.backButton}
                                    disabled={loading}
                                    onClick={handleBack}
                                    variant="outlined"
                                >
                                    <ArrowBackIcon />
                                </MUIButton>
                            )}

                            {activeStep === steps.length - 1 ? (
                                historyFormId ? (
                                    <MUIButton
                                        disabled={loading}
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={async () => {
                                            parameterStore.formid = '';
                                            saveForm();
                                        }}
                                    >
                                        Save as a form
                                    </MUIButton>
                                ) : null
                            ) : (
                                <MUIButton
                                    className={`${classes.nextButton} ${isStepValid && !loading ? 'enabled' : ''}`}
                                    onClick={handleNext}
                                    disabled={!isStepValid || loading}
                                    disableRipple
                                >
                                    NEXT
                                </MUIButton>
                            )}
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={4} sm={3} md={3}>
                    <AgentAssistPanel
                        tab="prequal"
                        onValueChangedNotes={(value) => valueChanged({...prequal?.notes, name: 'notes'}, value)}
                    />
                </Grid>
                <Dialog
                    open={saveConfirmationOpen}
                    onClose={() => cancelPendingSave()}
                    aria-labelledby="confirm-dialog-title"
                >
                    <DialogTitle id="confirm-dialog-title">Confirmation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>The SSN is associated with following forms:</DialogContentText>
                        <DialogContentText>
                            <ul>
                                {duplicateSsnForms.map(({formId, email}) => (
                                    <li key={formId}>
                                        {formId} - {email}
                                    </li>
                                ))}
                            </ul>
                        </DialogContentText>
                        <DialogContentText>Please double check before confirming save.</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => cancelPendingSave()} color="primary">
                            Cancel
                        </Button>
                        <Button color="primary" onClick={() => confirmPendingSave()}>
                            Confirm Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        );
    },
);
